import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Runtime detection - works in both localhost and production
const API = (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
  ? 'http://localhost:8001/api' 
  : '/api';

// Helper function to get full URL for assets
const getFullAssetUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}${url}`;
};

function BrokerIndexTemplate({ broker }) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const brokerLogo = broker?.logo_url || '';
  const brokerName = broker?.name || 'Trading Platform';

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-[#1e5a8e] py-4 px-4 shadow-md">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            {brokerLogo ? (
              <img 
                src={getFullAssetUrl(brokerLogo)} 
                alt={brokerName} 
                className="h-16 w-auto object-contain" 
              />
            ) : (
              <div className="text-white text-2xl font-bold">{brokerName}</div>
            )}
          </div>
          
          <div className="hidden md:flex items-center gap-6">
            <button 
              onClick={() => navigate('/login')}
              className="px-6 py-2 bg-white text-[#1e5a8e] rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Giriş Yap
            </button>
            <button 
              onClick={() => navigate('/login?mode=register')}
              className="px-6 py-2 bg-[#26c6da] text-white rounded-lg font-semibold hover:bg-[#1ea8c1] transition-colors"
            >
              Hesap Aç
            </button>
          </div>

          <button 
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden text-white"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 md:hidden" onClick={() => setMenuOpen(false)}>
          <div className="absolute right-0 top-0 bottom-0 w-80 bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <span className="font-bold text-gray-800">Menü</span>
              <button onClick={() => setMenuOpen(false)} className="text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <nav className="p-4 space-y-4">
              <button 
                onClick={() => navigate('/login')}
                className="w-full px-6 py-3 bg-[#1e5a8e] text-white rounded-lg font-semibold"
              >
                Giriş Yap
              </button>
              <button 
                onClick={() => navigate('/login?mode=register')}
                className="w-full px-6 py-3 bg-[#26c6da] text-white rounded-lg font-semibold"
              >
                Hesap Aç
              </button>
            </nav>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-[#1e5a8e] to-[#26c6da] py-20 px-4">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            {brokerName}
          </h1>
          <p className="text-xl md:text-2xl mb-8 opacity-90">
            Profesyonel Yatırım Platformu
          </p>
          <button 
            onClick={() => navigate('/login')}
            className="px-12 py-4 bg-white text-[#1e5a8e] rounded-lg text-xl font-bold hover:bg-gray-100 transition-colors"
          >
            Hemen Başla
          </button>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-800 mb-12">
            Hizmetlerimiz
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="w-16 h-16 bg-[#26c6da] rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Hisse Senedi</h3>
              <p className="text-gray-600">Borsa İstanbul'da işlem yapın</p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="w-16 h-16 bg-[#1e5a8e] rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Forex</h3>
              <p className="text-gray-600">Döviz piyasalarında işlem yapın</p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="w-16 h-16 bg-[#26c6da] rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">VİOP</h3>
              <p className="text-gray-600">Vadeli işlemler ve opsiyonlar</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#1e5a8e] py-8 px-4">
        <div className="max-w-6xl mx-auto text-center text-white">
          <p className="text-sm opacity-75">
            © 2025 {brokerName}. Tüm hakları saklıdır.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default BrokerIndexTemplate;
